import { SelectProps, Typography } from 'antd';
import {
  useCustomerReduced,
  useCustomerReducedList,
} from '@/core/domain/people/Customers/Customers.hooks';
import {
  CustomerReduced,
  ListCustomersReducedQuery,
} from '@/core/domain/people/Customers/Customers.types';
import { useEffect, useState } from 'react';
import InfiniteSelect from '../InfiniteSelect';
import { useDebounce } from '@/hooks';
import { applyCpfCnpjMask } from '@/utils/mask';
import useSelectOptions from '@/hooks/useSelectOptions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import validateCpf from '@/utils/functions/validateCpf';
import { isAppPortocar } from '@/index';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';

type Props = Omit<SelectProps, 'options'> & {
  showCpfCnpj?: boolean;
  queryFilter?: ListCustomersReducedQuery;
  onCustomerSelected?: (customer?: CustomerReduced) => void;
  queryEnable?: boolean;
};

const CustomersSelectField: React.FC<Props> = ({
  queryFilter,
  showCpfCnpj,
  onCustomerSelected,
  queryEnable = false,
  ...props
}) => {
  const { Text } = Typography;
  const [selectClicked, setSelectClicked] = useState<boolean>(queryEnable);
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);
  const [documentOption, setDocumentOption] = useState<{
    label: string;
    value: number;
  }>();

  const isMultiple = props.mode === 'multiple';

  const customerList = useCustomerReducedList(
    {
      rows: 10,
      search: search,
      active: true,
      ...queryFilter,
    },
    selectClicked && !props.disabled,
  );

  const customers = flatInfiniteData(customerList.data);

  const customerDetail = useCustomerReduced();

  const renderDocumentValue = (customer: CustomerReduced) => {
    if (!customer) return;

    if (customer.foreignDocument) {
      return customer.foreignDocument;
    }
    if (customer.cpfCnpj) {
      return applyCpfCnpjMask(customer.cpfCnpj);
    }

    return '-';
  };

  const customerOptions = useSelectOptions({
    list: customerList,
    detail: customerDetail,
    selectProps: props,
    labelFormatter: (item) => (
      <>
        <div key={item.id}>
          <Text>{item.name}</Text>
        </div>

        {showCpfCnpj && (
          <div>
            <Text type="secondary">{renderDocumentValue(item)}</Text>
          </div>
        )}
      </>
    ),
  });

  useEffect(() => {
    if (customerDetail.data?.id && !!onCustomerSelected) {
      onCustomerSelected(customerDetail.data);
    }
  }, [customerDetail.data, props.value]);

  useEffect(() => {
    if (!isAppPortocar) return;

    if (search && !customerOptions.length && !props.value) {
      if (
        !validateCpf(search) ||
        removeCpfCnpjMask(documentOption?.label) === search
      )
        return;

      setDocumentOption({
        label: String(applyCpfCnpjMask(search)),
        value: 0,
      });
    } else {
      setDocumentOption(undefined);
    }
  }, [customerOptions, props.value, search]);

  return (
    <InfiniteSelect
      {...props}
      fetchNextPage={customerList.fetchNextPage}
      hasNextPage={customerList.hasNextPage}
      loading={customerList.isFetching}
      options={documentOption ? [documentOption] : customerOptions}
      onSelect={(customerId: number) => {
        setSelectClicked(false);

        const selectedCustomer = customers.find(
          (customer) => customer.id === customerId,
        );

        if (!!selectedCustomer && !!onCustomerSelected) {
          onCustomerSelected(selectedCustomer);
        } else if (customerId === 0 && documentOption && onCustomerSelected) {
          onCustomerSelected({
            id: customerId,
            name: '',
            cpfCnpj: removeCpfCnpjMask(documentOption.label),
          });
        }
      }}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      allowClear
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      maxTagCount={isMultiple ? 'responsive' : undefined}
      maxTagTextLength={isMultiple ? 30 : undefined}
    />
  );
};

export default CustomersSelectField;
